import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { Toaster } from "sonner";
import { useEffect } from "react";
import { AuthProvider, useAuth } from "@/lib/auth";
import { TourProvider } from "@/components/tour/TourProvider";
import { BRAND } from "@/brand.config";
import "@/App.css";

// Public pages
import PublicLayout from "@/pages/public/PublicLayout";
import Home from "@/pages/public/Home";
import LoyaltyBenefits from "@/pages/public/LoyaltyBenefits";
import HowItWorks from "@/pages/public/HowItWorks";
import Rewards from "@/pages/public/Rewards";
import ReferralProgram from "@/pages/public/ReferralProgram";
import StoreLocator from "@/pages/public/StoreLocator";
import FAQs from "@/pages/public/FAQs";
import Privacy from "@/pages/public/Privacy";
import Terms from "@/pages/public/Terms";
import Contact from "@/pages/public/Contact";
import EarnPoints from "@/pages/public/EarnPoints";
import RedeemPoints from "@/pages/public/RedeemPoints";
import AboutProgram from "@/pages/public/AboutProgram";

// Auth
import EnterpriseLogin from "@/pages/auth/EnterpriseLogin";
import StoreLogin from "@/pages/auth/StoreLogin";
import CRMLogin from "@/pages/auth/CRMLogin";

// Admin
import AdminLayout from "@/pages/admin/AdminLayout";
import ControlCentre from "@/pages/admin/control_centre/ControlCentre";
import EventBus from "@/pages/admin/control_centre/EventBus";
import IntegrationHub from "@/pages/admin/control_centre/IntegrationHub";
import Journeys from "@/pages/admin/control_centre/Journeys";
import DecisionEngine from "@/pages/admin/control_centre/DecisionEngine";
import LoyaltyLedger from "@/pages/admin/control_centre/LoyaltyLedger";
import IdentityResolution from "@/pages/admin/control_centre/IdentityResolution";
import ConsentCentre from "@/pages/admin/control_centre/ConsentCentre";
import DigitalReceipts from "@/pages/admin/control_centre/DigitalReceipts";
import AccessControl from "@/pages/admin/control_centre/AccessControl";
import FeedbackCentre from "@/pages/admin/control_centre/FeedbackCentre";
import RewardsReferrals from "@/pages/admin/control_centre/RewardsReferrals";
import StoreService from "@/pages/admin/control_centre/StoreService";
import Commerce from "@/pages/admin/control_centre/Commerce";
import GrowthHub from "@/pages/admin/control_centre/GrowthHub";
import ReportBuilder from "@/pages/admin/control_centre/ReportBuilder";
import { ChooseYourReward, FeedbackForm, AppointmentView, Wallet } from "@/pages/public/CustomerPages";
import SuiteHome from "@/pages/suite/SuiteHome";
import ModuleLayout from "@/pages/suite/ModuleLayout";
import { ModuleDashboard, ModuleReports, ModuleReportBuilder, ModuleRules, ModuleCustomer } from "@/pages/suite/ModuleShell";
import CallingWorkspace from "@/pages/suite/CallingWorkspace";
import FeedbackHome from "@/pages/suite/feedback/FeedbackHome";
import Surveys from "@/pages/suite/feedback/Surveys";
import SurveyBuilder from "@/pages/suite/feedback/SurveyBuilder";
import { Broadcast, Invites } from "@/pages/suite/feedback/Distribution";
import { Responses, Detractors } from "@/pages/suite/feedback/Responses";
import { Funnel as CyrFunnel, Coupons as CyrCoupons, Redeem as CyrRedeem } from "@/pages/suite/rewards/CyrPages";
import Triggers from "@/pages/suite/feedback/Triggers";
import { ReceiptsHome, SendLog, CtaAnalytics } from "@/pages/suite/receipts/ReceiptsPages";
import TemplateDesigner from "@/pages/suite/receipts/TemplateDesigner";
import Profile from "@/pages/suite/customer360/Profile";
import ReceiptView from "@/pages/public/ReceiptView";
import PreferenceCentre from "@/pages/public/PreferenceCentre";
import ExecutiveCockpit from "@/pages/admin/ExecutiveCockpit";
import CommandCenter from "@/pages/admin/dashboards/CommandCenter";
import Customer360 from "@/pages/admin/Customer360";
import CustomerDetail from "@/pages/admin/CustomerDetail";
import LoyaltyConfigurator from "@/pages/admin/LoyaltyConfigurator";
import CouponEngine from "@/pages/admin/CouponEngine";
import CampaignManager from "@/pages/admin/CampaignManager";
import FundleBrain from "@/pages/admin/FundleBrain";
import MasterBrain from "@/pages/admin/MasterBrain";
import APIMonitor from "@/pages/admin/APIMonitor";
import UserManagement from "@/pages/admin/UserManagement";
import StoresPage from "@/pages/admin/Stores";
import NPSPage from "@/pages/admin/NPS";
import TicketsPage from "@/pages/admin/Tickets";
import TicketDetail from "@/pages/admin/TicketDetail";
import ReportsPage from "@/pages/admin/Reports";
import ItemMaster from "@/pages/admin/ItemMaster";
import CMSPage from "@/pages/admin/CMS";
import SalesDashboard from "@/pages/admin/dashboards/SalesDashboard";
import CustomerDashboard from "@/pages/admin/dashboards/CustomerDashboard";
import LoyaltyDashboard from "@/pages/admin/dashboards/LoyaltyDashboard";
import CampaignDashboard from "@/pages/admin/dashboards/CampaignDashboard";
import StoreDashboard from "@/pages/admin/dashboards/StoreDashboard";
import NPSDashboard from "@/pages/admin/dashboards/NPSDashboard";
import RFMDashboard from "@/pages/admin/dashboards/RFMDashboard";
import CohortsDashboard from "@/pages/admin/dashboards/CohortsDashboard";
import PointsDashboard from "@/pages/admin/dashboards/PointsDashboard";
import CampaignROIDashboard from "@/pages/admin/dashboards/CampaignROIDashboard";
import ExecutiveSummary from "@/pages/admin/dashboards/ExecutiveSummary";
import FormulaCatalog from "@/pages/admin/FormulaCatalog";
import TemplatesPage from "@/pages/admin/communications/TemplatesPage";
import ProviderSettingsPage from "@/pages/admin/communications/ProviderSettingsPage";
import BulkJobsPage from "@/pages/admin/communications/BulkJobsPage";
import MessageLogPage from "@/pages/admin/communications/MessageLogPage";
import AutoCampaignsPage from "@/pages/admin/AutoCampaignsPage";
import RawReportsPage from "@/pages/admin/RawReportsPage";
import DigestsPage from "@/pages/admin/DigestsPage";
import HistoricDataPage from "@/pages/admin/HistoricDataPage";
import VerifyLoadPage from "@/pages/admin/VerifyLoadPage";
import ReconciliationPage from "@/pages/admin/ReconciliationPage";
import SegmentBuilderPage from "@/pages/admin/SegmentBuilderPage";
import LiveMonitorPage from "@/pages/admin/LiveMonitorPage";
import POSCredentialsPage from "@/pages/admin/POSCredentialsPage";
import SearchRedeemPointsOTP from "@/pages/admin/support_desk/SearchRedeemPointsOTP";
import SearchRedeemCouponOTP from "@/pages/admin/support_desk/SearchRedeemCouponOTP";
import ReactivateCoupon from "@/pages/admin/support_desk/ReactivateCoupon";
import ReactivateRedeemPoints from "@/pages/admin/support_desk/ReactivateRedeemPoints";
import CustomerDeactivate from "@/pages/admin/support_desk/CustomerDeactivate";
import CustomerReactivate from "@/pages/admin/support_desk/CustomerReactivate";
import UnsubscribeCustomer from "@/pages/admin/support_desk/UnsubscribeCustomer";
import SupportDeskAuditLog from "@/pages/admin/support_desk/SupportDeskAuditLog";
import UpdateMobile from "@/pages/admin/support_desk/UpdateMobile";
import LegacyReportsHub from "@/pages/admin/legacy_reports/LegacyReportsHub";
import ShopperBillReport from "@/pages/admin/ShopperBillReport";
import StoreKPIReport from "@/pages/admin/StoreKPIReport";
import CRMCustomerReport from "@/pages/admin/CRMCustomerReport";
import KPITrends from "@/pages/admin/KPITrends";
import DownloadsCenter from "@/pages/admin/DownloadsCenter";
import LRCustomerData from "@/pages/admin/legacy_reports/CustomerData";
import LRTransactionData from "@/pages/admin/legacy_reports/TransactionData";
import LRRepeatCustomers from "@/pages/admin/legacy_reports/RepeatCustomers";
import LRTopCustomers from "@/pages/admin/legacy_reports/TopCustomers";
import LRFraudReport from "@/pages/admin/legacy_reports/FraudReport";
import LRPendingBills from "@/pages/admin/legacy_reports/PendingBills";
import LRFeedbackData from "@/pages/admin/legacy_reports/FeedbackData";
import LRMissedCalls from "@/pages/admin/legacy_reports/MissedCalls";
import LRLocationWise from "@/pages/admin/legacy_reports/LocationWiseCustomers";
import LRExpiryPoints from "@/pages/admin/legacy_reports/ExpiryPoints";
import LRActiveCoupons from "@/pages/admin/legacy_reports/ActiveCoupons";
import StoreOps from "@/pages/store/StoreOps";
import DemoLanding from "@/pages/public/DemoLanding";

function ProtectedRoute({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="font-display text-2xl">Loading…</div></div>;
  if (!user) return <Navigate to="/enterprise/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/admin" replace />;
  return children;
}

function ModuleIndex() {
  const { module } = useParams();
  if (module === "feedback") return <FeedbackHome />;
  if (module === "receipts") return <ReceiptsHome />;
  return <ModuleDashboard />;
}

function App() {
  // Inject brand colour palette as CSS variables once at mount.
  // Changing values in brand.config.js will reflect everywhere using
  // the existing --blackberrys-* CSS variables — no per-component edits.
  useEffect(() => {
    const r = document.documentElement.style;
    r.setProperty("--blackberrys-black", BRAND.colors.black);
    r.setProperty("--blackberrys-cream", BRAND.colors.cream);
    r.setProperty("--blackberrys-burgundy", BRAND.colors.burgundy);
    r.setProperty("--blackberrys-burgundy-deep", BRAND.colors.burgundyDeep);
    r.setProperty("--blackberrys-champagne", BRAND.colors.champagne);
    r.setProperty("--blackberrys-champagne-light", BRAND.colors.champagneLight);
  }, []);

  return (
    <AuthProvider>
      <BrowserRouter>
        <TourProvider>
          <Toaster position="top-right" richColors />
          <Routes>
            {/* Public site */}
            <Route element={<PublicLayout />}>
            <Route path="/" element={<Home />} />
            <Route path="/about-program" element={<AboutProgram />} />
            <Route path="/loyalty-benefits" element={<LoyaltyBenefits />} />
            <Route path="/rewards" element={<Rewards />} />
            <Route path="/how-it-works" element={<HowItWorks />} />
            <Route path="/earn-points" element={<EarnPoints />} />
            <Route path="/redeem-points" element={<RedeemPoints />} />
            <Route path="/referral-program" element={<ReferralProgram />} />
            <Route path="/store-locator" element={<StoreLocator />} />
            <Route path="/faqs" element={<FAQs />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/contact" element={<Contact />} />
          </Route>

          {/* Auth */}
          <Route path="/enterprise/login" element={<EnterpriseLogin />} />
          <Route path="/store/login" element={<StoreLogin />} />
          <Route path="/crm/login" element={<CRMLogin />} />

          {/* Public self-running product demo */}
          <Route path="/demo" element={<DemoLanding />} />

          {/* Customer-facing secure links */}
          <Route path="/r/:token" element={<ReceiptView />} />
          <Route path="/preferences/:token" element={<PreferenceCentre />} />
          <Route path="/cyr" element={<ChooseYourReward />} />
          <Route path="/feedback/:token" element={<FeedbackForm />} />
          <Route path="/appointments/:token" element={<AppointmentView />} />
          <Route path="/wallet" element={<Wallet />} />

          {/* Module Suite — every module is a full application with its own left menu; Loyalty Engine = /admin (full platform) */}
          <Route path="/suite" element={<ProtectedRoute><SuiteHome /></ProtectedRoute>} />
          <Route path="/m/:module" element={<ProtectedRoute><ModuleLayout /></ProtectedRoute>}>
            <Route index element={<ModuleIndex />} />
            <Route path="reports" element={<ModuleReports />} />
            <Route path="report-builder" element={<ModuleReportBuilder />} />
            <Route path="rules" element={<ModuleRules />} />
            <Route path="customer" element={<ModuleCustomer />} />
            {/* Calling & Lead Management */}
            <Route path="pipeline" element={<CallingWorkspace view="pipeline" />} />
            <Route path="workspace" element={<CallingWorkspace view="pipeline" />} />
            <Route path="tasks" element={<CallingWorkspace view="list" />} />
            <Route path="new" element={<CallingWorkspace view="new" />} />
            <Route path="sources" element={<CallingWorkspace view="sources" />} />
            <Route path="calls" element={<CallingWorkspace view="logs" />} />
            <Route path="agents" element={<CallingWorkspace view="agents" />} />
            <Route path="tickets" element={<TicketsPage />} />
            <Route path="tickets/:id" element={<TicketDetail />} />
            <Route path="appointments" element={<StoreService />} />
            {/* NPS & Feedback */}
            <Route path="surveys" element={<Surveys />} />
            <Route path="surveys/:fid" element={<SurveyBuilder />} />
            <Route path="broadcast" element={<Broadcast />} />
            <Route path="invites" element={<Invites />} />
            <Route path="responses" element={<Responses />} />
            <Route path="detractors" element={<Detractors />} />
            <Route path="triggers" element={<Triggers />} />
            <Route path="nps-inbox" element={<NPSPage />} />
            <Route path="nps-dashboard" element={<NPSDashboard />} />
            {/* Choose Your Reward / STYLO */}
            <Route path="funnel" element={<CyrFunnel />} />
            <Route path="coupons" element={<CyrCoupons />} />
            <Route path="redeem" element={<CyrRedeem />} />
            <Route path="referrals" element={<RewardsReferrals />} />
            {/* Receipts · Commerce · Vouchers · Consent · Identity */}
            <Route path="receipts" element={<DigitalReceipts />} />
            <Route path="log" element={<SendLog />} />
            <Route path="cta" element={<CtaAnalytics />} />
            <Route path="template" element={<TemplateDesigner />} />
            <Route path="profile" element={<Profile />} />
            <Route path="commerce" element={<Commerce />} />
            <Route path="consent" element={<ConsentCentre />} />
            <Route path="identity" element={<IdentityResolution />} />
            <Route path="customers" element={<Customer360 />} />
            <Route path="customers/:id" element={<CustomerDetail />} />
            <Route path="support-desk/search-redeem-points-otp" element={<SearchRedeemPointsOTP />} />
            <Route path="support-desk/search-redeem-coupon-otp" element={<SearchRedeemCouponOTP />} />
            <Route path="support-desk/reactivate-coupon" element={<ReactivateCoupon />} />
            <Route path="support-desk/reactivate-redeem-points" element={<ReactivateRedeemPoints />} />
            <Route path="support-desk/customer-deactivate" element={<CustomerDeactivate />} />
            <Route path="support-desk/customer-reactivate" element={<CustomerReactivate />} />
            <Route path="support-desk/update-mobile" element={<UpdateMobile />} />
            <Route path="support-desk/unsubscribe" element={<UnsubscribeCustomer />} />
            <Route path="support-desk/audit-log" element={<SupportDeskAuditLog />} />
            {/* Journeys & Marketing */}
            <Route path="journeys" element={<Journeys />} />
            <Route path="events" element={<EventBus />} />
            <Route path="campaigns" element={<CampaignManager />} />
            <Route path="auto-campaigns" element={<AutoCampaignsPage />} />
            <Route path="segments" element={<SegmentBuilderPage />} />
            <Route path="growth" element={<GrowthHub />} />
            <Route path="templates" element={<TemplatesPage />} />
            <Route path="message-log" element={<MessageLogPage />} />
            <Route path="bulk-jobs" element={<BulkJobsPage />} />
            <Route path="provider-settings" element={<ProviderSettingsPage />} />
            {/* Integrations · Control */}
            <Route path="hub" element={<IntegrationHub />} />
            <Route path="api-monitor" element={<APIMonitor />} />
            <Route path="pos-credentials" element={<POSCredentialsPage />} />
            <Route path="objects" element={<ControlCentre />} />
            <Route path="access" element={<AccessControl />} />
            <Route path="users" element={<UserManagement />} />
          </Route>

          {/* Admin */}
          <Route path="/admin" element={<ProtectedRoute><AdminLayout /></ProtectedRoute>}>
            <Route index element={<LiveMonitorPage />} />
            <Route path="control-centre" element={<ControlCentre />} />
            <Route path="control-centre/events" element={<EventBus />} />
            <Route path="control-centre/integrations" element={<IntegrationHub />} />
            <Route path="control-centre/journeys" element={<Journeys />} />
            <Route path="control-centre/decision-engine" element={<DecisionEngine />} />
            <Route path="control-centre/ledger" element={<LoyaltyLedger />} />
            <Route path="control-centre/identity" element={<IdentityResolution />} />
            <Route path="control-centre/consent" element={<ConsentCentre />} />
            <Route path="control-centre/receipts" element={<DigitalReceipts />} />
            <Route path="control-centre/access" element={<AccessControl />} />
            <Route path="control-centre/feedback" element={<FeedbackCentre />} />
            <Route path="control-centre/rewards" element={<RewardsReferrals />} />
            <Route path="control-centre/service" element={<StoreService />} />
            <Route path="control-centre/commerce" element={<Commerce />} />
            <Route path="control-centre/growth" element={<GrowthHub />} />
            <Route path="control-centre/report-builder" element={<ReportBuilder />} />
            <Route path="dashboards" element={<CommandCenter />} />
            <Route path="dashboards/command-center" element={<CommandCenter />} />
            <Route path="cockpit" element={<ExecutiveCockpit />} />
            <Route path="dashboards/sales" element={<SalesDashboard />} />
            <Route path="dashboards/customers" element={<CustomerDashboard />} />
            <Route path="dashboards/loyalty" element={<LoyaltyDashboard />} />
            <Route path="dashboards/campaigns" element={<CampaignDashboard />} />
            <Route path="dashboards/stores" element={<StoreDashboard />} />
            <Route path="dashboards/nps" element={<NPSDashboard />} />
            <Route path="dashboards/rfm" element={<RFMDashboard />} />
            <Route path="dashboards/cohorts" element={<CohortsDashboard />} />
            <Route path="dashboards/points" element={<PointsDashboard />} />
            <Route path="dashboards/campaign-roi" element={<CampaignROIDashboard />} />
            <Route path="dashboards/executive-summary" element={<ExecutiveSummary />} />
            <Route path="formula-catalog" element={<FormulaCatalog />} />
            <Route path="communications/templates" element={<TemplatesPage />} />
            <Route path="communications/message-log" element={<MessageLogPage />} />
            <Route path="communications/bulk-jobs" element={<BulkJobsPage />} />
            <Route path="communications/settings" element={<ProviderSettingsPage />} />
            <Route path="customers" element={<Customer360 />} />
            <Route path="customers/:id" element={<CustomerDetail />} />
            <Route path="loyalty" element={<LoyaltyConfigurator />} />
            <Route path="coupons" element={<CouponEngine />} />
            <Route path="campaigns" element={<CampaignManager />} />
            <Route path="auto-campaigns" element={<ProtectedRoute roles={["super_admin","brand_admin","crm_manager","marketing_manager"]}><AutoCampaignsPage /></ProtectedRoute>} />
            <Route path="raw-reports" element={<RawReportsPage />} />
            <Route path="ai" element={<FundleBrain />} />
            <Route path="master-brain" element={<MasterBrain />} />
            <Route path="api-monitor" element={<APIMonitor />} />
            <Route path="users" element={<ProtectedRoute roles={["super_admin","brand_admin","corporate","it_admin"]}><UserManagement /></ProtectedRoute>} />
            <Route path="stores" element={<StoresPage />} />
            <Route path="items" element={<ItemMaster />} />
            <Route path="cms" element={<CMSPage />} />
            <Route path="nps" element={<NPSPage />} />
            <Route path="tickets" element={<TicketsPage />} />
            <Route path="tickets/:id" element={<TicketDetail />} />
            <Route path="reports" element={<ReportsPage />} />
            <Route path="reports/digests" element={<DigestsPage />} />
            <Route path="historic-data" element={<ProtectedRoute roles={["super_admin","brand_admin","crm_manager","marketing_manager"]}><HistoricDataPage /></ProtectedRoute>} />
            <Route path="verify-load" element={<ProtectedRoute roles={["super_admin","brand_admin","crm_manager","marketing_manager"]}><VerifyLoadPage /></ProtectedRoute>} />
            <Route path="reconciliation" element={<ProtectedRoute roles={["super_admin","brand_admin","corporate","it_admin"]}><ReconciliationPage /></ProtectedRoute>} />
            <Route path="segments" element={<ProtectedRoute roles={["super_admin","brand_admin","crm_manager","marketing_manager"]}><SegmentBuilderPage /></ProtectedRoute>} />
            <Route path="live-monitor" element={<LiveMonitorPage />} />
            <Route path="pos-credentials" element={<ProtectedRoute roles={["super_admin","brand_admin","corporate","it_admin"]}><POSCredentialsPage /></ProtectedRoute>} />

            {/* Support Desk — L1 operations */}
            <Route path="support-desk/search-redeem-points-otp" element={<ProtectedRoute roles={["super_admin","brand_admin","support_agent","crm_manager"]}><SearchRedeemPointsOTP /></ProtectedRoute>} />
            <Route path="support-desk/search-redeem-coupon-otp" element={<ProtectedRoute roles={["super_admin","brand_admin","support_agent","crm_manager"]}><SearchRedeemCouponOTP /></ProtectedRoute>} />
            <Route path="support-desk/reactivate-coupon" element={<ProtectedRoute roles={["super_admin","brand_admin","support_agent"]}><ReactivateCoupon /></ProtectedRoute>} />
            <Route path="support-desk/reactivate-redeem-points" element={<ProtectedRoute roles={["super_admin","brand_admin","support_agent"]}><ReactivateRedeemPoints /></ProtectedRoute>} />
            <Route path="support-desk/customer-deactivate" element={<ProtectedRoute roles={["super_admin","brand_admin","support_agent"]}><CustomerDeactivate /></ProtectedRoute>} />
            <Route path="support-desk/customer-reactivate" element={<ProtectedRoute roles={["super_admin","brand_admin","support_agent"]}><CustomerReactivate /></ProtectedRoute>} />
            <Route path="support-desk/update-mobile" element={<ProtectedRoute roles={["super_admin","brand_admin","support_agent"]}><UpdateMobile /></ProtectedRoute>} />
            <Route path="support-desk/unsubscribe" element={<ProtectedRoute roles={["super_admin","brand_admin","support_agent"]}><UnsubscribeCustomer /></ProtectedRoute>} />
            <Route path="support-desk/audit-log" element={<ProtectedRoute roles={["super_admin","brand_admin","support_agent","crm_manager"]}><SupportDeskAuditLog /></ProtectedRoute>} />

            {/* Legacy Reports — mirrors fundlezone.com Analytics */}
            <Route path="legacy-reports" element={<ProtectedRoute><LegacyReportsHub /></ProtectedRoute>} />
            <Route path="legacy-reports/customer-data" element={<ProtectedRoute><LRCustomerData /></ProtectedRoute>} />
            <Route path="legacy-reports/transaction-data" element={<ProtectedRoute><LRTransactionData /></ProtectedRoute>} />
            <Route path="legacy-reports/repeat-customers" element={<ProtectedRoute><LRRepeatCustomers /></ProtectedRoute>} />
            <Route path="legacy-reports/top-customers" element={<ProtectedRoute><LRTopCustomers /></ProtectedRoute>} />
            <Route path="legacy-reports/fraud-report" element={<ProtectedRoute><LRFraudReport /></ProtectedRoute>} />
            <Route path="legacy-reports/pending-bills" element={<ProtectedRoute><LRPendingBills /></ProtectedRoute>} />
            <Route path="legacy-reports/feedback-data" element={<ProtectedRoute><LRFeedbackData /></ProtectedRoute>} />
            <Route path="legacy-reports/missed-calls" element={<ProtectedRoute><LRMissedCalls /></ProtectedRoute>} />
            <Route path="legacy-reports/location-wise-customers" element={<ProtectedRoute><LRLocationWise /></ProtectedRoute>} />
            <Route path="legacy-reports/expiry-points" element={<ProtectedRoute><LRExpiryPoints /></ProtectedRoute>} />
            <Route path="legacy-reports/active-coupons" element={<ProtectedRoute><LRActiveCoupons /></ProtectedRoute>} />
            <Route path="reports/shopper-bills" element={<ProtectedRoute><ShopperBillReport /></ProtectedRoute>} />
            <Route path="reports/store-kpi" element={<ProtectedRoute><StoreKPIReport /></ProtectedRoute>} />
            <Route path="reports/crm-customers" element={<ProtectedRoute><CRMCustomerReport /></ProtectedRoute>} />
            <Route path="reports/kpi-trends" element={<ProtectedRoute><KPITrends /></ProtectedRoute>} />
            <Route path="downloads" element={<ProtectedRoute><DownloadsCenter /></ProtectedRoute>} />
          </Route>

          {/* Store ops portal */}
          <Route path="/store" element={<ProtectedRoute roles={["store_manager","store_staff","super_admin","brand_admin"]}><StoreOps /></ProtectedRoute>} />

          <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </TourProvider>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
